import React from 'react';
import { usePermissions } from 'hooks/usePermissions';

interface PermissionGateProps {
  resource: string;
  action?: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
  requireAll?: boolean;
  requireAny?: boolean;
  requireEdit?: boolean;
  requireManage?: boolean;
}

export const PermissionGate: React.FC<PermissionGateProps> = ({
  resource,
  action,
  children,
  fallback = null,
  requireAll = false,
  requireAny = false,
  requireEdit = false,
  requireManage = false,
}) => {
  const {
    hasPermission,
    canRead,
    canCreate,
    canUpdate,
    canDelete,
    hasAnyAccess,
    canEdit,
    canManage,
  } = usePermissions();

  const hasAccess = (): boolean => {
    if (action) {
      return hasPermission(resource, action);
    }

    if (requireAll) {
      return (
        canRead(resource) &&
        canCreate(resource) &&
        canUpdate(resource) &&
        canDelete(resource)
      );
    }

    if (requireAny) {
      return hasAnyAccess(resource);
    }

    if (requireEdit) {
      return canEdit(resource);
    }

    if (requireManage) {
      return canManage(resource);
    }

    return canRead(resource);
  };

  if (!hasAccess()) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};

export const ReadPermissionGate: React.FC<
  Omit<PermissionGateProps, 'action'>
> = props => <PermissionGate {...props} action="read" />;

export const CreatePermissionGate: React.FC<
  Omit<PermissionGateProps, 'action'>
> = props => <PermissionGate {...props} action="create" />;

export const UpdatePermissionGate: React.FC<
  Omit<PermissionGateProps, 'action'>
> = props => <PermissionGate {...props} action="update" />;

export const DeletePermissionGate: React.FC<
  Omit<PermissionGateProps, 'action'>
> = props => <PermissionGate {...props} action="delete" />;

export const EditPermissionGate: React.FC<
  Omit<PermissionGateProps, 'action' | 'requireEdit'>
> = props => <PermissionGate {...props} requireEdit={true} />;

export const ManagePermissionGate: React.FC<
  Omit<PermissionGateProps, 'action' | 'requireManage'>
> = props => <PermissionGate {...props} requireManage={true} />;

export const AnyPermissionGate: React.FC<
  Omit<PermissionGateProps, 'action' | 'requireAny'>
> = props => <PermissionGate {...props} requireAny={true} />;
